<template>
  <header class="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm">
    <!-- Top Bar -->
    <div class="bg-white border-b border-gray-200">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-end items-center h-12 gap-6">
          <!-- Phone -->
          <a href="tel:400-852-6298" class="flex items-center gap-2 text-gray-700 hover:text-[#0A2463] transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-[#0A2463]">
              <path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384"></path>
            </svg>
            <span class="text-sm">{{ $t('navBar.phone') }}</span>
          </a>

          <!-- Language Switcher -->
          <div class="flex items-center gap-2">
            <button
              @click="setLanguage('zh-ch')"
              :class="locale === 'zh-ch' ? 'bg-[#0A2463] text-white' : 'text-gray-700 hover:bg-gray-100'"
              class="px-3 py-1 text-sm transition-all duration-200 rounded flex items-center gap-1.5"
            >
              <span class="text-base">🇨🇳</span>
              <span>{{ $t('navBar.chinese') }}</span>
            </button>
            <button
              @click="setLanguage('en-us')"
              :class="locale === 'en-us' ? 'bg-[#0A2463] text-white' : 'text-gray-700 hover:bg-gray-100'"
              class="px-3 py-1 text-sm transition-all duration-200 rounded flex items-center gap-1.5"
            >
              <span class="text-base">🇬🇧</span>
              <span>{{ $t('navBar.english') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Navigation -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between items-center h-20">
        <!-- Logo -->
        <router-link to="/" class="flex items-center gap-3">
          <img :src="assets('images/logo.png')" alt="LEVIY Logo" class="w-12 h-12 rounded-xl" />
          <div class="text-[#0A2463]">
            <div class="text-2xl tracking-wider font-semibold">LEVIY {{ $t('navBar.brandName') }}</div>
            <div class="text-[10px] tracking-widest opacity-70">{{ $t('navBar.brandSubtitle') }}</div>
          </div>
        </router-link>

        <!-- Desktop Navigation -->
        <nav class="hidden lg:flex items-center space-x-6">
          <router-link to="/" class="text-gray-700 hover:text-[#0A2463] transition-colors duration-200">{{ $t('navBar.home') }}</router-link>

          <!-- Business Services Dropdown -->
          <div
            class="relative"
            @mouseenter="businessMenuOpen = true"
            @mouseleave="businessMenuOpen = false"
          >
            <button class="flex items-center space-x-1 text-gray-700 hover:text-[#0A2463] transition-colors duration-200 py-2">
              <span>{{ $t('navBar.businessServices') }}</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="m6 9 6 6 6-6"></path>
              </svg>
            </button>

            <!-- Dropdown Menu -->
            <Transition
              enter-active-class="transition ease-out duration-200"
              enter-from-class="opacity-0 translate-y-2"
              enter-to-class="opacity-100 translate-y-0"
              leave-active-class="transition ease-in duration-150"
              leave-from-class="opacity-100 translate-y-0"
              leave-to-class="opacity-0 translate-y-2"
            >
              <div
                v-if="businessMenuOpen"
                class="absolute left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-100 py-2"
              >
                <a
                  v-for="(item, index) in businessServices"
                  :key="index"
                  :href="item.href"
                  class="block px-6 py-3 text-sm text-gray-600 hover:text-[#0A2463] hover:bg-gray-50 transition-colors"
                >
                  {{ $t(item.label) }}
                </a>
              </div>
            </Transition>
          </div>

          <!-- Legal Services Dropdown -->
          <div
            class="relative"
            @mouseenter="legalMenuOpen = true"
            @mouseleave="legalMenuOpen = false"
          >
            <button class="flex items-center space-x-1 text-gray-700 hover:text-[#0A2463] transition-colors duration-200 py-2">
              <span>{{ $t('navBar.legalServices') }}</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="m6 9 6 6 6-6"></path>
              </svg>
            </button>

            <!-- Dropdown Menu -->
            <Transition
              enter-active-class="transition ease-out duration-200"
              enter-from-class="opacity-0 translate-y-2"
              enter-to-class="opacity-100 translate-y-0"
              leave-active-class="transition ease-in duration-150"
              leave-from-class="opacity-100 translate-y-0"
              leave-to-class="opacity-0 translate-y-2"
            >
              <div
                v-if="legalMenuOpen"
                class="absolute left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-100 py-2"
              >
                <a
                  v-for="(item, index) in legalServices"
                  :key="index"
                  :href="item.href"
                  class="block px-6 py-3 text-sm text-gray-600 hover:text-[#0A2463] hover:bg-gray-50 transition-colors"
                >
                  {{ $t(item.label) }}
                </a>
              </div>
            </Transition>
          </div>

          <router-link to="/knowledge" class="text-gray-700 hover:text-[#0A2463] transition-colors duration-200">{{ $t('navBar.knowledge') }}</router-link>
          <router-link to="/about" class="text-gray-700 hover:text-[#0A2463] transition-colors duration-200">{{ $t('navBar.about') }}</router-link>
          <router-link to="/contact" class="text-gray-700 hover:text-[#0A2463] transition-colors duration-200">{{ $t('navBar.contact') }}</router-link>
        </nav>

        <!-- Mobile menu button -->
        <button
          @click="mobileMenuOpen = !mobileMenuOpen"
          class="lg:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100"
        >
          <svg v-if="!mobileMenuOpen" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M4 5h16"></path>
            <path d="M4 12h16"></path>
            <path d="M4 19h16"></path>
          </svg>
          <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 6 6 18"></path>
            <path d="m6 6 12 12"></path>
          </svg>
        </button>
      </div>
    </div>

    <!-- Mobile menu -->
    <Transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="mobileMenuOpen" class="lg:hidden border-t border-gray-200 bg-white">
        <div class="px-4 py-6 space-y-4">
          <!-- Home -->
          <div>
            <router-link to="/" @click="closeMobileMenu" class="block text-gray-700">{{ $t('navBar.home') }}</router-link>
          </div>

          <!-- Business Services with submenu -->
          <div>
            <button
              @click="toggleMobileBusinessMenu"
              class="block w-full text-left text-gray-700 flex items-center justify-between"
            >
              <span>{{ $t('navBar.businessServices') }}</span>
              <svg
                :class="{ 'rotate-180': mobileBusinessMenuOpen }"
                xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round transition-transform duration-200"
              >
                <path d="m6 9 6 6 6-6"></path>
              </svg>
            </button>
            <div v-if="mobileBusinessMenuOpen" class="pl-4 mt-2 space-y-2">
              <a
                v-for="(item, index) in businessServicesFull"
                :key="index"
                :href="item.href"
                @click="closeMobileMenu"
                class="block w-full text-left text-sm text-gray-600"
              >
                {{ $t(item.label) }}
              </a>
            </div>
          </div>

          <!-- Legal Services with submenu -->
          <div>
            <button
              @click="toggleMobileLegalMenu"
              class="block w-full text-left text-gray-700 flex items-center justify-between"
            >
              <span>{{ $t('navBar.legalServices') }}</span>
              <svg
                :class="{ 'rotate-180': mobileLegalMenuOpen }"
                xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round transition-transform duration-200"
              >
                <path d="m6 9 6 6 6-6"></path>
              </svg>
            </button>
            <div v-if="mobileLegalMenuOpen" class="pl-4 mt-2 space-y-2">
              <a
                v-for="(item, index) in legalServicesFull"
                :key="index"
                :href="item.href"
                @click="closeMobileMenu"
                class="block w-full text-left text-sm text-gray-600"
              >
                {{ $t(item.label) }}
              </a>
            </div>
          </div>

          <!-- Other links -->
          <div>
            <router-link to="/knowledge" @click="closeMobileMenu" class="block text-gray-700">{{ $t('navBar.knowledge') }}</router-link>
          </div>
          <div>
            <router-link to="/about" @click="closeMobileMenu" class="block text-gray-700">{{ $t('navBar.about') }}</router-link>
          </div>
          <div>
            <router-link to="/contact" @click="closeMobileMenu" class="block text-gray-700">{{ $t('navBar.contact') }}</router-link>
          </div>

          <!-- Phone -->
          <a href="tel:400-852-6298" class="flex items-center gap-2 text-[#0A2463] pt-2 border-t hover:opacity-80 transition-opacity">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384"></path>
            </svg>
            <span class="text-sm">{{ $t('navBar.phone') }}</span>
          </a>

          <!-- Language Switcher -->
          <div class="flex items-center space-x-2 pt-2">
            <button
              @click="setLanguage('zh-ch')"
              :class="locale === 'zh-ch' ? 'bg-[#0A2463] text-white' : 'text-gray-700 border border-gray-300'"
              class="flex-1 px-3 py-2 text-sm transition-all duration-200 rounded-md flex items-center justify-center gap-1"
            >
              <span class="text-base">🇨🇳</span>
              <span>{{ $t('navBar.chinese') }}</span>
            </button>
            <button
              @click="setLanguage('en-us')"
              :class="locale === 'en-us' ? 'bg-[#0A2463] text-white' : 'text-gray-700 border border-gray-300'"
              class="flex-1 px-3 py-2 text-sm transition-all duration-200 rounded-md flex items-center justify-center gap-1"
            >
              <span class="text-base">🇬🇧</span>
              <span>{{ $t('navBar.english') }}</span>
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </header>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { assets } from '../utils/util'

const { locale } = useI18n()
const mobileMenuOpen = ref(false)
const mobileBusinessMenuOpen = ref(false)
const mobileLegalMenuOpen = ref(false)
const businessMenuOpen = ref(false)
const legalMenuOpen = ref(false)

const setLanguage = (lang) => {
  locale.value = lang
  localStorage.setItem('leviy-lang', lang)
}

const closeMobileMenu = () => {
  mobileMenuOpen.value = false
  mobileBusinessMenuOpen.value = false
  mobileLegalMenuOpen.value = false
}

const toggleMobileBusinessMenu = () => {
  mobileBusinessMenuOpen.value = !mobileBusinessMenuOpen.value
}

const toggleMobileLegalMenu = () => {
  mobileLegalMenuOpen.value = !mobileLegalMenuOpen.value
}

// Business services menu items (same for desktop and mobile)
const businessServices = [
  { href: '/business-services#company-registration', label: 'footer.companyRegistration' },
  { href: '/business-services#banking', label: 'footer.banking' },
  { href: '/business-services#accounting', label: 'footer.accounting' },
  { href: '/business-services#company-search', label: 'footer.companySearch' },
  { href: '/business-services#cross-border-investment', label: 'footer.crossBorderInvestment' },
  { href: '/business-services#tax-planning', label: 'footer.taxPlanning' },
  { href: '/business-services#international-solutions', label: 'footer.internationalSolutions' },
]

// Alias for mobile (same as desktop)
const businessServicesFull = businessServices

// Legal services menu items (same as footer)
const legalServices = [
  { href: '/legal-services#legal-opinion', label: 'footer.legalOpinionFull' },
  { href: '/legal-services#notarization', label: 'footer.notarizationFull' },
  { href: '/legal-services#due-diligence', label: 'footer.dueDiligence' },
  { href: '/legal-services#contract-drafting', label: 'footer.contractDraftingFull' },
  { href: '/legal-services#property-investigation', label: 'footer.propertyInvestigation' },
  { href: '/legal-services#arbitration-enforcement', label: 'footer.arbitrationEnforcementFull' },
  { href: '/legal-services#general-legal', label: 'footer.generalLegal' },
]

// Full legal services menu for mobile
const legalServicesFull = [
  { href: '/legal-services#legal-opinion', label: 'footer.legalOpinionFull' },
  { href: '/legal-services#notarization', label: 'footer.notarizationFull' },
  { href: '/legal-services#due-diligence', label: 'footer.dueDiligence' },
  { href: '/legal-services#contract-drafting', label: 'footer.contractDraftingFull' },
  { href: '/legal-services#property-investigation', label: 'footer.propertyInvestigation' },
  { href: '/legal-services#arbitration-enforcement', label: 'footer.arbitrationEnforcementFull' },
  { href: '/legal-services#general-legal', label: 'footer.generalLegal' },
]
</script>
